import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { StatusPill } from '../../components/StatusPills';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Search } from 'lucide-react';
import { verificationLabel, missingFieldLabels } from '../../lib/canonicalOrg';

const TYPE_OPTIONS = [
  { v: '', label: 'All types' },
  { v: 'council', label: 'Council' },
  { v: 'fostering_agency', label: 'Fostering agency' },
  { v: 'childrens_home', label: "Children's home" },
  { v: 'adoption', label: 'Adoption' },
  { v: 'school', label: 'School' },
  { v: 'doctor', label: 'Doctor / GP' },
  { v: 'hospital', label: 'Hospital' },
  { v: 'police', label: 'Police' },
  { v: 'mental_health', label: 'Mental health' },
];

const AdminOrganisations = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [verified, setVerified] = useState('');
  const [missingEmail, setMissingEmail] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listOrgs({
        q: q || undefined,
        type: type || undefined,
        verified: verified || undefined,
        missing_email: missingEmail || undefined,
      });
      setRows(res.data?.results || []);
      setTotal(res.data?.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, type, verified, missingEmail]);

  return (
    <div className="space-y-5" data-testid="admin-orgs-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">Canonical organisations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total} record{total === 1 ? '' : 's'} · search, filter, edit, verify or merge.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 grid md:grid-cols-[1fr_180px_180px_auto] gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, alias or email"
              className="pl-9"
              data-testid="admin-orgs-search"
            />
          </div>
          <Select value={type || 'all'} onValueChange={(v) => setType(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="admin-orgs-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.v || 'all'} value={o.v || 'all'}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={verified || 'all'} onValueChange={(v) => setVerified(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="admin-orgs-verified">
              <SelectValue placeholder="Verified" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Verified</SelectItem>
              <SelectItem value="no">Unverified</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={missingEmail ? 'default' : 'secondary'}
            size="sm"
            className="min-h-[48px]"
            onClick={() => setMissingEmail((v) => !v)}
            data-testid="admin-orgs-missing-email-toggle"
          >
            Missing email
          </Button>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orgs-table">
            <thead className="bg-ground">
              <tr className="text-left">
                <th className="py-2.5 px-4 font-medium text-muted-foreground">Name</th>
                <th className="py-2.5 px-4 font-medium text-muted-foreground">Type</th>
                <th className="py-2.5 px-4 font-medium text-muted-foreground">Privacy email</th>
                <th className="py-2.5 px-4 font-medium text-muted-foreground">Verified</th>
                <th className="py-2.5 px-4 font-medium text-muted-foreground">Linked users</th>
                <th className="py-2.5 px-4 font-medium text-muted-foreground">SARs</th>
                <th className="py-2.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No matches</td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-rule hover:bg-ground"
                  data-testid={`admin-orgs-row-${r.id}`}
                >
                  <td className="py-2.5 px-4 font-medium text-foreground">{r.name}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{r.type}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{r.privacy_email || '—'}</td>
                  <td className="py-2.5 px-4">
                    <span className="inline-flex">
                      <StatusPill tone={r.admin_verified ? 'done' : 'needs'} size="sm">{verificationLabel(r)}</StatusPill>
                    </span>
                    {r.admin_verified && missingFieldLabels(r).length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">Missing {missingFieldLabels(r).join(', ')}</p>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-foreground">{r.linked_user_count ?? 0}</td>
                  <td className="py-2.5 px-4 text-foreground">{r.total_sars ?? 0}</td>
                  <td className="py-2.5 px-4 text-right">
                    <Link to={`/admin/organisations/${r.id}`}>
                      <Button variant="ghost" size="sm">View</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminOrganisations;
